import React, { useEffect, useState } from 'react';
import { FaClock, FaUser, FaFilter, FaBed, FaCalendarAlt, FaIdCard, FaPlus } from 'react-icons/fa';

import './AdmissionsListTable.css';
import axios from 'axios';
import ENDPOINTS, { API_BASE_URL } from '../../Constants/ApiEndpoints';
import { calculateDuration, dateFormatter, timeFormatter } from '../../CommonComponents/Utils/DateTimeFormatter';
import SearchBar from './SearchBar';
import DateRangePickerComponent from '../../CommonComponents/DateRangePickerComponent';
import { Pagination, PaginationItem } from '@mui/material';
import { toast } from 'react-toastify';

export default function AdmissionsListTable({
    startDate,
    endDate,
    onDateChange,
    onSearch,
    admissionFilters,
    onNewAdmission,
    onSelectPatient
}) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showPatientFilter, setShowPatientFilter] = useState(false);
    const [patientFilter, setPatientFilter] = useState('');

    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
    const [popoverDirMap, setPopoverDirMap] = useState({});

    const handleRowMouseEnter = (e, index) => {
        setHoveredRowIndex(index);
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < 250 && spaceAbove > 200) {
            setPopoverDirMap((prev) => ({ ...prev, [index]: 'above' }));
        } else {
            setPopoverDirMap((prev) => ({ ...prev, [index]: 'below' }));
        }
    };

    const handleRowMouseLeave = () => {
        setHoveredRowIndex(null);
    };

    const [activeTab, setActiveTab] = useState("admitted"); // "admitted" or "discharged"

    const [selectedPendingAdmission, setSelectedPendingAdmission] = useState(null);
    const [roomType, setRoomType] = useState('General Ward');
    const [bedNumber, setBedNumber] = useState('');
    const [floor, setFloor] = useState('');
    const [savingRoomBed, setSavingRoomBed] = useState(false);
    const [allUnassignedAdmissions, setAllUnassignedAdmissions] = useState([]);

    const handleSaveRoomBed = async () => {
        if (!bedNumber.trim()) {
            toast.error("Please enter a Bed Number");
            return;
        }
        setSavingRoomBed(true);
        try {
            await axios.patch(`${API_BASE_URL}/updateAdmissionRoomBed/${selectedPendingAdmission.admissionId}`, {
                roomType,
                bedNumber,
                floor
            });
            toast.success("Room & Bed assigned successfully!");
            setSelectedPendingAdmission(null);
            fetchData();
            fetchUnassignedAdmissions();
        } catch (err) {
            console.error("Error updating room and bed:", err);
            toast.error("Failed to assign Room & Bed.");
        } finally {
            setSavingRoomBed(false);
        }
    };

    // Pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [counts, setCounts] = useState({ admitted: 0, discharged: 0, todayAdmitted: 0, todayDischarged: 0 });

    const formatDate = (date) => {
        if (!date) return '';
        const pad = (n) => n.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    };

    const isTodayRange = (sDate, eDate) => {
        if (!sDate || !eDate) return false;
        const today = new Date();
        return (
            sDate.getDate() === today.getDate() &&
            sDate.getMonth() === today.getMonth() &&
            sDate.getFullYear() === today.getFullYear() &&
            eDate.getDate() === today.getDate() &&
            eDate.getMonth() === today.getMonth() &&
            eDate.getFullYear() === today.getFullYear()
        );
    };

    const handleTodayAdmittedClick = () => {
        if (activeTab === "admitted" && isTodayRange(startDate, endDate)) {
            // Reset back to all records (1970-01-01 to today)
            if (onDateChange) {
                onDateChange(new Date('1970-01-01'), new Date());
            }
        } else {
            setActiveTab("admitted");
            const today = new Date();
            if (onDateChange) {
                onDateChange(today, today);
            }
        }
    };

    const handleTodayDischargedClick = () => {
        if (activeTab === "discharged" && isTodayRange(startDate, endDate)) {
            // Reset back to all records (1970-01-01 to today)
            if (onDateChange) {
                onDateChange(new Date('1970-01-01'), new Date());
            }
        } else {
            setActiveTab("discharged");
            const today = new Date();
            if (onDateChange) {
                onDateChange(today, today);
            }
        }
    };

    // Style helper for room type badges
    const getRoomTypeStyle = (roomType) => {
        const type = (roomType || '').toLowerCase();
        if (type.includes('icu')) return { bg: '#fef2f2', color: '#991b1b', border: '#fca5a5' };
        if (type.includes('emergency')) return { bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' };
        if (type.includes('deluxe') || type.includes('single')) return { bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' };
        if (type.includes('sharing')) return { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' };
        return { bg: '#f8fafc', color: '#475569', border: '#cbd5e1' };
    };

    // Fetch admissions data from API with filters
    const fetchData = async (currentPage = page, currentSize = pageSize) => {
        setLoading(true);

        try {
            const params = {
                fromDate: formatDate(startDate),
                toDate: formatDate(endDate),
                admissionStatus: activeTab.toUpperCase(), // 'ADMITTED' or 'DISCHARGED'
                page: currentPage,
                size: currentSize,
            };

            if (admissionFilters?.type && admissionFilters?.query) {
                if (admissionFilters.type === 'MR No') params.mrNumber = admissionFilters.query;
                if (admissionFilters.type === 'Name') params.name = admissionFilters.query;
                if (admissionFilters.type === 'Phone') params.phoneNumber = admissionFilters.query;
            }

            const response = await axios.get(`${ENDPOINTS.SEARCH_ADMISSIONS_BY_FILTERS}`, { params });
            setRecords(response.data.records || []);
            setCounts({
                admitted: response.data.admitted || 0,
                discharged: response.data.discharged || 0,
                todayAdmitted: response.data.todayAdmitted || 0,
                todayDischarged: response.data.todayDischarged || 0,
            });

            setTotalPages(response.data.totalPages || 0);
            setTotalElements(response.data.totalElements || 0);
        } catch (error) {
            console.error('Error fetching Admissions data:', error);
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchUnassignedAdmissions = async () => {
        try {
            const params = {
                fromDate: formatDate(startDate),
                toDate: formatDate(endDate),
                admissionStatus: 'ADMITTED',
                page: 0,
                size: 500,
            };
            const response = await axios.get(`${ENDPOINTS.SEARCH_ADMISSIONS_BY_FILTERS}`, { params });
            const admittedList = response.data.records || [];
            const pending = admittedList
                .filter(
                    (r) => !r.dischargeDate && (!r.roomType || !r.bedNumber || r.roomType === '' || r.bedNumber === '')
                )
                .sort((a, b) => {
                    const dateA = a.admissionDate ? new Date(`${a.admissionDate}T${a.admissionTime || '00:00:00'}`).getTime() : 0;
                    const dateB = b.admissionDate ? new Date(`${b.admissionDate}T${b.admissionTime || '00:00:00'}`).getTime() : 0;
                    if (dateA !== dateB && !isNaN(dateA) && !isNaN(dateB)) {
                        return dateB - dateA;
                    }
                    return (Number(b.admissionId) || 0) - (Number(a.admissionId) || 0);
                });
            setAllUnassignedAdmissions(pending);
        } catch (error) {
            console.error('Error fetching unassigned admissions:', error);
        }
    };

    useEffect(() => {
        fetchData(0, pageSize);
        fetchUnassignedAdmissions();
    }, [startDate, endDate, admissionFilters]);

    useEffect(() => {
        setPage(0);
    }, [activeTab]);

    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize, activeTab]);

    // Client side filter by patient name
    const displayedRecords = records.filter((record) =>
        (record.patientName || '').toLowerCase().includes(patientFilter.toLowerCase())
    );

    const filteredRecords =
        activeTab === "discharged"
            ? displayedRecords.filter(r => r.dischargeDate)
            : displayedRecords.filter(r => !r.dischargeDate);

    // Sort admissions in descending order so the latest admissions appear first
    const sortedFilteredRecords = [...filteredRecords].sort((a, b) => {
        const dateA = a.admissionDate ? new Date(`${a.admissionDate}T${a.admissionTime || '00:00:00'}`).getTime() : 0;
        const dateB = b.admissionDate ? new Date(`${b.admissionDate}T${b.admissionTime || '00:00:00'}`).getTime() : 0;
        if (dateA !== dateB && !isNaN(dateA) && !isNaN(dateB)) {
            return dateB - dateA;
        }
        return (Number(b.admissionId) || 0) - (Number(a.admissionId) || 0);
    });

    return (
        <div className="unified-master-card">
            {/* Top Accent Gradient Bar */}
            <div className="master-card-accent-bar" />

            {/* Master Header Toolbar: Fused Search + Date Picker + CTA Button (Matches OP Appointments) */}
            <div className="master-toolbar-header">
                <div className="toolbar-left-group">
                    <div className="toolbar-search-box">
                        <SearchBar onSearch={onSearch} />
                    </div>
                    <div className="toolbar-date-picker">
                        <DateRangePickerComponent
                            initialStartDate={startDate}
                            initialEndDate={endDate}
                            onDateChange={onDateChange}
                        />
                    </div>
                </div>

                <button
                    className="master-new-appt-btn"
                    onClick={onNewAdmission}
                >
                    <FaPlus size={13} /> New Admission
                </button>
            </div>

            {/* Pending Assignment Alert Banner - Enterprise Modernized */}
            {allUnassignedAdmissions.length > 0 && (
                <div className="pending-assign-alert">
                    <div className="pending-alert-content">
                        <div className="pending-alert-header">
                            <div className="pending-alert-icon-badge">
                                <FaBed className="pulse-icon" />
                            </div>
                            <div className="pending-alert-text">
                                <div className="pending-title-row">
                                    <h4 className="pending-main-title">Pending IP Admission Action Required</h4>
                                    <span className="pending-count-chip">{allUnassignedAdmissions.length} Pending</span>
                                </div>
                                <p className="pending-sub-desc">
                                    Patient(s) admitted from Outpatient require Room Type & Bed Number assignment.
                                </p>
                            </div>
                        </div>

                        <div className="pending-btn-group">
                            {allUnassignedAdmissions.map((adm, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className="assign-pending-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setSelectedPendingAdmission(adm);
                                        setRoomType(adm.roomType || 'General Ward');
                                        setBedNumber(adm.bedNumber || '');
                                        setFloor(adm.floor || adm.floorNumber || '');
                                    }}
                                >
                                    <FaBed size={12} />
                                    <span>Assign {adm.patientName}</span>
                                    <span className="mr-badge">MR: {adm.mrNumber}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Unified Segmented Control Tabs (macOS / Vercel style - Matches OP Appointments) */}
            <div className="master-tabs-bar">
                <div className="tabs-list">
                    <button
                        className={`tab-btn ${activeTab === "admitted" ? "active-tab-btn" : ""}`}
                        onClick={() => setActiveTab("admitted")}
                    >
                        Admitted Patients ({counts.admitted || 0})
                    </button>
                    <button
                        className={`tab-btn ${activeTab === "discharged" ? "active-tab-btn" : ""}`}
                        onClick={() => setActiveTab("discharged")}
                    >
                        Discharged Patients ({counts.discharged || 0})
                    </button>
                </div>

                <div className="status-chips-list">
                    <button
                        type="button"
                        className={`status-chip waiting-chip interactive-chip ${activeTab === "admitted" && isTodayRange(startDate, endDate) ? "chip-active" : ""}`}
                        onClick={handleTodayAdmittedClick}
                        title="Click to filter today's admitted patients"
                    >
                        Today Admitted: {counts.todayAdmitted || 0}
                    </button>
                    <button
                        type="button"
                        className={`status-chip completed-chip interactive-chip ${activeTab === "discharged" && isTodayRange(startDate, endDate) ? "chip-active" : ""}`}
                        onClick={handleTodayDischargedClick}
                        title="Click to filter today's discharged patients"
                    >
                        Today Discharged: {counts.todayDischarged || 0}
                    </button>
                </div>
            </div>

            {/* Master Data Table Container */}
            <div className="master-table-container">
                {loading ? (
                    <div className="table-loading-state">
                        <FaClock size={32} color="#64748b" />
                        <p>Loading Inpatient Admissions...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="master-data-table">
                            <thead>
                                <tr>
                                    {/* Table Header with column specific classes for responsive width distribution */
                                    /* Proportional column widths total 100% ensuring seamless fit at 90% zoom without clipping */}
                                    <th className="col-admission-date">ADMISSION DATE</th>
                                    <th className="patient-col-header col-patient">
                                        <div
                                            className="patient-header-content"
                                            onClick={() => setShowPatientFilter((prev) => !prev)}
                                            title="Click to filter by Patient Name"
                                        >
                                            <span>PATIENT</span>
                                            <FaFilter
                                                size={11}
                                                className={`filter-funnel-icon ${patientFilter ? 'active' : ''}`}
                                            />
                                        </div>

                                        {showPatientFilter && (
                                            <div className="header-filter-popover" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="text"
                                                    value={patientFilter}
                                                    onChange={(e) => setPatientFilter(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    placeholder="Filter by name..."
                                                    autoFocus
                                                />
                                            </div>
                                        )}
                                    </th>
                                    <th className="col-mr-id">ID</th>
                                    <th className="col-floor">FLOOR</th>
                                    <th className="col-room-type">ROOM TYPE</th>
                                    <th className="col-room-bed">ROOM / BED</th>
                                    <th className="col-stay-duration">LENGTH OF STAY</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedFilteredRecords.length > 0 ? (
                                    sortedFilteredRecords.map((record, index) => (
                                        <tr
                                            key={index}
                                            onClick={() => onSelectPatient(record)}
                                            onMouseEnter={(e) => handleRowMouseEnter(e, index)}
                                            onMouseLeave={handleRowMouseLeave}
                                            className={`table-data-row ${(record.flagged || record.isFlagged) ? 'flagged-row' : ''} ${record.emergency ? 'emergency-row' : ''}`}
                                        >
                                            {/* Admission Date & Formatted Time: wraps onto 2 lines naturally */}
                                            <td className="col-admission-date">
                                                <div className="admission-datetime-cell">
                                                    <span className="admission-date-text">
                                                        <FaCalendarAlt size={11} color="#94a3b8" /> {dateFormatter(record.admissionDate)}
                                                    </span>
                                                    <span className="admission-time-text">
                                                        {timeFormatter(record.admissionTime)}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Patient Avatar, Full Name & Emergency Chip: wrapped in flex container allowing multi-line wrap */}
                                            <td className="col-patient">
                                                <div className="patient-name-cell">
                                                    <div className="patient-avatar-circle">
                                                        {record.photo ? (
                                                            <img src={`data:image/jpeg;base64,${record.photo}`} alt={record.patientName} className="patient-avatar-img" />
                                                        ) : (
                                                            <FaUser size={12} color="#2563eb" />
                                                        )}
                                                    </div>
                                                    <span className="patient-fullname">{record.patientName}</span>
                                                    {record.emergency && (
                                                        <span className="emergency-badge-chip">🚨 Emergency</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* ID Pill with Hover Photo Popover */}
                                            <td className="col-mr-id" style={{ position: 'relative' }}>
                                                <span className="code-pill mr-pill">
                                                    <FaIdCard size={10} /> {record.mrNumber}
                                                </span>

                                                {/* Hover Photo Lightbox Popover */}
                                                {hoveredRowIndex === index && !showPatientFilter && (
                                                    <div
                                                        className={`patient-hover-popover ${popoverDirMap[index] === 'above' ? 'popover-above' : 'popover-below'}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="popover-avatar-wrap">
                                                            <img
                                                                src={record.photo ? `data:image/jpeg;base64,${record.photo}` : "/VThospitalsIcon.png"}
                                                                alt={record.patientName}
                                                                className="popover-avatar-img"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Floor Number */}
                                            <td className="col-floor">
                                                {record.floor || record.floorNumber ? (
                                                    <span className="info-cell-text">{record.floor || record.floorNumber}</span>
                                                ) : (
                                                    <span className="muted-dash">N/A</span>
                                                )}
                                            </td>

                                            {/* Room Type Badge: multi-line wrapping enabled for large badge texts */}
                                            <td className="col-room-type">
                                                {record.roomType ? (
                                                    <span
                                                        className="room-type-badge"
                                                        style={{
                                                            backgroundColor: getRoomTypeStyle(record.roomType).bg,
                                                            color: getRoomTypeStyle(record.roomType).color,
                                                            border: `1px solid ${getRoomTypeStyle(record.roomType).border}`
                                                        }}
                                                    >
                                                        {record.roomType}
                                                    </span>
                                                ) : (
                                                    <span className="muted-dash">-</span>
                                                )}
                                            </td>

                                            {/* Room / Bed Number: multi-line wrapping enabled when bed description is large */}
                                            <td className="col-room-bed">
                                                {record.bedNumber ? (
                                                    <span className="bed-pill">
                                                        <FaBed size={11} color="#2563eb" /> {record.bedNumber}
                                                    </span>
                                                ) : (
                                                    <span className="muted-dash">Unassigned</span>
                                                )}
                                            </td>

                                            {/* Length of Stay: compact with wrap support for long durations */}
                                            <td className="col-stay-duration">
                                                <span className="duration-pill">
                                                    <FaClock size={11} color="#64748b" /> {calculateDuration(
                                                        record.admissionDate,
                                                        record.admissionTime,
                                                        record.dischargeDate,
                                                        record.dischargeTime
                                                    )}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="empty-table-cell">
                                            No admission records found matching criteria.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Master Pagination Footer */}
            <div className="master-pagination-footer">
                <span className="pagination-summary-text">
                    Showing page <strong>{page + 1}</strong> of <strong>{totalPages || 1}</strong> ({totalElements} admissions)
                </span>
                <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(e, value) => setPage(value - 1)}
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            components={{
                                previous: () => <span>Previous</span>,
                                next: () => <span>Next</span>,
                            }}
                        />
                    )}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                />
            </div>

            {/* Complete Admission Modal - Enterprise Redesign */}
            {selectedPendingAdmission && (
                <div
                    className="assign-room-modal-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setSelectedPendingAdmission(null);
                        }
                    }}
                >
                    <div className="assign-room-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="assign-modal-header">
                            <div className="assign-modal-title-group">
                                <div className="assign-title-icon">
                                    <FaBed />
                                </div>
                                <div>
                                    <h3>Complete Inpatient Admission</h3>
                                    <p className="assign-modal-subtitle">Assign Room Type & Bed Number to finalize admission</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="assign-close-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPendingAdmission(null);
                                }}
                                title="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="assign-modal-body">
                            {/* Patient Overview Details Card */}
                            <div className="assign-patient-summary-card">
                                <div className="patient-avatar-mini">
                                    <FaUser size={14} />
                                </div>
                                <div className="patient-summary-info">
                                    <h4 className="summary-patient-name">{selectedPendingAdmission.patientName}</h4>
                                    <div className="summary-badges-row">
                                        <span className="summary-badge mr-chip">MR: {selectedPendingAdmission.mrNumber}</span>
                                        <span className="summary-badge id-chip">ID: #{selectedPendingAdmission.admissionId}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Form Fields */}
                            <div className="assign-form-group">
                                <label className="assign-field-label">Select Room Type <span className="required-star">*</span></label>
                                <div className="assign-input-wrapper">
                                    <select
                                        className="assign-select-control"
                                        value={roomType}
                                        onChange={(e) => setRoomType(e.target.value)}
                                    >
                                        <option value="General Ward">General Ward</option>
                                        <option value="Sharing Room">Sharing Room</option>
                                        <option value="Single Room">Single Room</option>
                                        <option value="Deluxe Room">Deluxe Room</option>
                                        <option value="ICU">ICU (Intensive Care Unit)</option>
                                        <option value="Emergency Ward">Emergency Ward</option>
                                    </select>
                                </div>
                            </div>

                            <div className="assign-form-group">
                                <label className="assign-field-label">Bed Number <span className="required-star">*</span></label>
                                <div className="assign-input-wrapper">
                                    <input
                                        type="text"
                                        className="assign-input-control"
                                        value={bedNumber}
                                        onChange={(e) => setBedNumber(e.target.value)}
                                        placeholder="e.g. Bed B-104"
                                    />
                                </div>
                            </div>

                            <div className="assign-form-group">
                                <label className="assign-field-label">Floor Number</label>
                                <div className="assign-input-wrapper">
                                    <input
                                        type="text"
                                        className="assign-input-control"
                                        value={floor}
                                        onChange={(e) => setFloor(e.target.value)}
                                        placeholder="e.g. Floor 2"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="assign-modal-footer">
                            <button
                                type="button"
                                className="assign-cancel-btn"
                                onClick={() => setSelectedPendingAdmission(null)}
                                disabled={savingRoomBed}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="assign-submit-btn"
                                onClick={handleSaveRoomBed}
                                disabled={savingRoomBed}
                            >
                                <FaBed size={13} />
                                <span>{savingRoomBed ? "Saving Assignment..." : "Confirm Room & Bed"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
